import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  createStyles,
  Group,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {CollaborationCreateOrUpdate} from 'auth/PermissionConstants';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import navigateToURL from 'lib/navigateToURL';
import {agentHomepageUrl} from 'llm/routes/UrlUtils';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import StepNav, {StepItemStatus, StepNavSize, StepNavVariant} from 'ui-library/StepNav';
import * as DateUtil from 'util/DateUtil';

import MemoryViewerPanel from './MemoryViewerPanel';
import RoutineAccessTab from './RoutineAccessTab';
import {
  canCurrentUserModifyRoutine,
  getSelectableRunAsModes,
  isRoutineRunAsDisabled,
} from './routineAuthorization';
import RoutineCreateCollaboratorsStep from './RoutineCreateCollaboratorsStep';
import RoutineDataAccess from './RoutineDataAccess';
import RoutineForm from './RoutineForm';
import {canSaveRoutine, sanitizeTriggerConfig} from './routineFormValidation';
import RoutineRunsTab from './RoutineRunsTab';
import {
  buildMessageBlocks,
  EventTriggerConfig,
  extractFileBlocks,
  extractPromptFromBlocks,
  FileMessageBlock,
  gqlBlocksToMessageBlocks,
  LLMFeatureAccessUpdate,
  RoutineRunAs,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import useAllowedEventTriggers from './useAllowedEventTriggers';
import useRoutine, {RoutineQueryData} from './useRoutine';
import useRoutineFileUpload from './useRoutineFileUpload';
import useRoutineForm, {
  AccessLevel,
  getDefaultEventTriggerConfig,
  getDefaultTimeTriggerConfig,
  RoutineFormState,
  shallowFormEqual,
} from './useRoutineForm';
import useRoutineMutations from './useRoutineMutations';

const DEFAULT_AGENT_TYPE = 'ASSISTANT';

export type RoutineDetailTab = 'access' | 'data-access' | 'details' | 'runs';

export enum RoutineCreateWizardStep {
  SETUP = 'setup',
  DATA_ACCESS = 'data-access',
  COLLABORATORS = 'collaborators',
}

export type DetailPanelMode =
  | {type: 'edit'; routineId: string}
  | {type: 'create'; example?: RoutineFormState; exampleId?: string};

interface RoutineDetailPanelProps {
  mode: DetailPanelMode;
  onClose: () => void;
  onAgentHomepageNavigate?: () => void;
  onMutationSuccess: () => Promise<void>;
  onRoutineCreated?: (routineId: string) => void;
  onCopyRequest?: (formState: RoutineFormState, sourceRoutineId: string) => void;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
  /** When set, opens the create wizard on this step (used by Storybook). */
  initialCreateWizardStep?: RoutineCreateWizardStep;
  /** Forces the create collaborator wizard when routine sharing is disabled (Storybook). */
  enableCreateWizard?: boolean;
  /** Opens edit mode on a specific tab, for settings deep links. */
  initialEditTab?: RoutineDetailTab;
}

const useStyles = createStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabs: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabPanel: {
    flex: '1 1 auto',
    minHeight: 0,
    paddingTop: theme.spacing.xxl,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
    // Containing block for panel content so descendant overflow does not inflate .modal-body scrollHeight.
    position: 'relative',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.gray[4],
      borderRadius: theme.radius.sm,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.colors.gray[0],
    },
  },
  createHeader: {
    borderBottom: `0.125rem solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
    '& .step-item-label': {
      marginLeft: 6,
    },
  },
  stepNav: {
    paddingTop: theme.spacing.xs,
  },
  createFooter: {
    borderTop: `0.125rem solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
  },
  backNavHeader: {
    marginBottom: theme.spacing.sm,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing.md,
  },
  tabsList: {
    // Each tab already has md of horizontal padding, so this remainder lines the label up with the 3xl inset.
    paddingLeft: `calc(${theme.spacing['3xl']} - ${theme.spacing.md})`,
    paddingRight: `calc(${theme.spacing['3xl']} - ${theme.spacing.md})`,
  },
}));

function getDefaultCreateState(
  timeBasedEnabled: boolean,
  eventBasedEnabled: boolean,
  memoryEnabledDefault: boolean
): RoutineFormState {
  const defaultRunAs = getSelectableRunAsModes()[0] ?? RoutineRunAs.CREATOR;
  if (timeBasedEnabled && !eventBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.TIME_BASED,
      triggerConfig: getDefaultTimeTriggerConfig(),
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      runAs: defaultRunAs,
      overrideRunAs: null,
      memoryEnabled: memoryEnabledDefault,
    };
  }
  if (eventBasedEnabled && !timeBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: getDefaultEventTriggerConfig(),
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      runAs: defaultRunAs,
      overrideRunAs: null,
      memoryEnabled: memoryEnabledDefault,
    };
  }
  return {
    name: '',
    description: '',
    prompt: '',
    fileBlocks: [],
    triggerType: null,
    triggerConfig: {},
    allowedApprovalCategories: [],
    agentWorkspaceFolderId: null,
    runAs: defaultRunAs,
    overrideRunAs: null,
    memoryEnabled: memoryEnabledDefault,
  };
}

function buildFormStateFromQuery(
  data: RoutineQueryData['routine'],
  authorshipEnabled: boolean
): RoutineFormState | null {
  if (data == null) {
    return null;
  }
  const messageBlocks = gqlBlocksToMessageBlocks(data.messageBlocks);
  return {
    name: data.name ?? '',
    description: data.description ?? '',
    prompt: extractPromptFromBlocks(messageBlocks),
    fileBlocks: extractFileBlocks(messageBlocks),
    triggerType: data.triggerType,
    triggerConfig: withDefaultObjectScopes(
      data.triggerConfig as Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>,
      data.triggerType,
      authorshipEnabled
    ),
    allowedApprovalCategories: data.allowedApprovalCategories,
    agentWorkspaceFolderId:
      data.workspaceFolder != null ? getAccessibleOrInaccessibleId(data.workspaceFolder) : null,
    runAs: data.runAs,
    overrideRunAs: null,
    memoryEnabled: data.memoryEnabled,
  };
}

// Default a prefilled event config with no object_scopes to "viewable" when the section is shown.
// This applies to configs the user did not build from scratch — saved routines on load (which
// predate the feature but are already view-only on the backend) and example prefills — so they stay
// valid/saveable and reflect the backend default. A blank new routine is intentionally left empty
// so the user makes an explicit scope choice.
export function withDefaultObjectScopes(
  triggerConfig: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>,
  triggerType: TriggerType | null,
  authorshipEnabled: boolean
): Partial<TimeTriggerConfig> | Partial<EventTriggerConfig> {
  if (!authorshipEnabled || triggerType !== TriggerType.EVENT_BASED) {
    return triggerConfig;
  }
  const eventConfig = triggerConfig as Partial<EventTriggerConfig>;
  if ((eventConfig.object_scopes ?? []).length > 0) {
    return triggerConfig;
  }
  return {...eventConfig, object_scopes: ['viewable']};
}

function BackNavHeader({onClick, className}: {onClick: () => void; className?: string}): JSX.Element {
  return (
    <Group spacing="xs" className={className}>
      <ActionIcon aria-label="Back to Agents" onClick={onClick} size="sm" variant="ghost">
        <BenchlingIcon icon="arrow-left" />
      </ActionIcon>
      <Text size="lg" fw={600}>
        Agents
      </Text>
    </Group>
  );
}

interface CreateWizardStepDefinition {
  key: RoutineCreateWizardStep;
  title: string;
  metadata: string;
  nextButtonLabel: string;
  enabled: boolean;
}

interface CreateWizardFooterButtonConfig {
  label: string;
  onClick: () => void;
  disabled: boolean;
  loading?: boolean;
}

function buildCreateWizardSteps(
  dataAccessStepEnabled: boolean,
  collaboratorsStepEnabled: boolean
): Array<CreateWizardStepDefinition> {
  return [
    {
      key: RoutineCreateWizardStep.SETUP,
      title: 'Set up the agent',
      metadata: 'What it does, when it runs',
      nextButtonLabel: 'Add collaborators',
      enabled: true,
    },
    {
      key: RoutineCreateWizardStep.DATA_ACCESS,
      title: 'Grant data access',
      metadata: 'What it can access',
      nextButtonLabel: 'Grant data access',
      enabled: dataAccessStepEnabled,
    },
    {
      key: RoutineCreateWizardStep.COLLABORATORS,
      title: 'Add collaborators',
      metadata: 'Who can use it',
      nextButtonLabel: 'Add collaborators',
      enabled: collaboratorsStepEnabled,
    },
  ];
}

function useCreateStepNav({
  dataAccessStepEnabled,
  collaboratorsStepEnabled,
  initialStepKey,
  isCreateMode,
  canAdvanceFromStep,
  isSaving,
  onFinish,
  onCollaboratorsStepExit,
}: {
  dataAccessStepEnabled: boolean;
  collaboratorsStepEnabled: boolean;
  initialStepKey: RoutineCreateWizardStep;
  isCreateMode: boolean;
  canAdvanceFromStep: (stepKey: RoutineCreateWizardStep) => boolean;
  isSaving: boolean;
  onFinish: () => void;
  onCollaboratorsStepExit?: () => void;
}): {
  showStepNav: boolean;
  hideRoutineFormFooter: boolean;
  collaboratorsStepEnabled: boolean;
  enabledSteps: Array<CreateWizardStepDefinition>;
  currentStepKey: RoutineCreateWizardStep;
  setCurrentStepKey: (step: RoutineCreateWizardStep) => void;
  statusByStep: Record<RoutineCreateWizardStep, StepItemStatus | null>;
  isCurrentStep: (stepKey: RoutineCreateWizardStep) => boolean;
  footer: {
    back: CreateWizardFooterButtonConfig | null;
    primary: CreateWizardFooterButtonConfig | null;
  };
} {
  const steps = useMemo(
    () => buildCreateWizardSteps(dataAccessStepEnabled, collaboratorsStepEnabled),
    [collaboratorsStepEnabled, dataAccessStepEnabled]
  );
  const enabledSteps = useMemo(
    () => (isCreateMode ? steps.filter((step) => step.enabled) : []),
    [isCreateMode, steps]
  );
  const showStepNav = enabledSteps.length > 1;
  const defaultStepKey = enabledSteps[0]?.key ?? RoutineCreateWizardStep.SETUP;
  const [currentStepKey, setCurrentStepKey] = useState<RoutineCreateWizardStep>(initialStepKey);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }
    if (enabledSteps.some((step) => step.key === currentStepKey)) {
      return;
    }
    if (enabledSteps.some((step) => step.key === initialStepKey)) {
      setCurrentStepKey(initialStepKey);
      return;
    }
    // Keep waiting on `initialStepKey` until optional steps (e.g. data access) become enabled.
    if (currentStepKey !== initialStepKey) {
      setCurrentStepKey(defaultStepKey);
    }
  }, [isCreateMode, currentStepKey, defaultStepKey, enabledSteps, initialStepKey]);

  const currentStepIndex = enabledSteps.findIndex((step) => step.key === currentStepKey);
  const resolvedIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
  const previousStep = enabledSteps[resolvedIndex - 1] ?? null;
  const nextStep = enabledSteps[resolvedIndex + 1] ?? null;
  const resolvedCurrentStepKey = enabledSteps[resolvedIndex]?.key ?? defaultStepKey;
  const isFirstStep = resolvedIndex === 0;
  const isLastStep = resolvedIndex === enabledSteps.length - 1;

  const statusByStep = useMemo(() => {
    const status = Object.fromEntries(steps.map((step) => [step.key, null])) as Record<
      RoutineCreateWizardStep,
      StepItemStatus | null
    >;
    for (const [index, step] of enabledSteps.entries()) {
      // Prior steps intentionally use `null` (no status icon), not `StepItemStatus.DONE`: DONE styling
      // does not fit this flat wizard StepNav variant and was an explicit product decision.
      status[step.key] = index < resolvedIndex ? null : StepItemStatus.UNVISITED;
    }
    return status;
  }, [enabledSteps, resolvedIndex, steps]);

  const updateCurrentStepKey = useCallback(
    (step: RoutineCreateWizardStep) => {
      if (
        resolvedCurrentStepKey === RoutineCreateWizardStep.COLLABORATORS &&
        step !== RoutineCreateWizardStep.COLLABORATORS
      ) {
        onCollaboratorsStepExit?.();
      }
      setCurrentStepKey(step);
    },
    [onCollaboratorsStepExit, resolvedCurrentStepKey]
  );

  const goToPreviousStep = useCallback(() => {
    if (previousStep != null) {
      updateCurrentStepKey(previousStep.key);
    }
  }, [previousStep, updateCurrentStepKey]);

  const goToNextStep = useCallback(() => {
    if (nextStep != null) {
      updateCurrentStepKey(nextStep.key);
    }
  }, [nextStep, updateCurrentStepKey]);

  const isCurrentStep = useCallback(
    (stepKey: RoutineCreateWizardStep) => resolvedCurrentStepKey === stepKey,
    [resolvedCurrentStepKey]
  );

  const footer = useMemo((): {
    back: CreateWizardFooterButtonConfig | null;
    primary: CreateWizardFooterButtonConfig | null;
  } => {
    const back =
      isFirstStep || enabledSteps.length <= 1
        ? null
        : {
            label: 'Back',
            onClick: goToPreviousStep,
            disabled: isSaving,
          };

    const primary = isLastStep
      ? {
          label: 'Finish setup',
          onClick: onFinish,
          disabled: isSaving || !canAdvanceFromStep(resolvedCurrentStepKey),
          loading: isSaving,
        }
      : {
          label: nextStep?.nextButtonLabel != null ? `Next step: ${nextStep.nextButtonLabel}` : 'Next step',
          onClick: goToNextStep,
          disabled: !canAdvanceFromStep(resolvedCurrentStepKey),
        };

    return {back, primary};
  }, [
    canAdvanceFromStep,
    enabledSteps.length,
    goToNextStep,
    goToPreviousStep,
    isFirstStep,
    isLastStep,
    isSaving,
    nextStep,
    onFinish,
    resolvedCurrentStepKey,
  ]);

  return {
    showStepNav,
    hideRoutineFormFooter: showStepNav,
    collaboratorsStepEnabled,
    enabledSteps,
    currentStepKey: resolvedCurrentStepKey,
    setCurrentStepKey: updateCurrentStepKey,
    statusByStep,
    isCurrentStep,
    footer,
  };
}

export default function RoutineDetailPanel({
  mode,
  onClose,
  onAgentHomepageNavigate = noop,
  onMutationSuccess,
  onRoutineCreated,
  onCopyRequest = noop,
  timeBasedEnabled,
  eventBasedEnabled,
  initialCreateWizardStep,
  enableCreateWizard = false,
  initialEditTab = 'details',
}: RoutineDetailPanelProps): JSX.Element {
  const {classes, cx, theme} = useStyles();
  const {
    llmEnableAgentMemory,
    llmEnableRoutineConfigAuthorship,
    llmEnableRoutineHomepage,
    llmEnableRoutinesSharing,
  } = useRolloutFlags();
  const authorshipEnabled = llmEnableRoutineConfigAuthorship === true;
  const runsTabEnabled = llmEnableRoutineHomepage !== true;
  const isEdit = mode.type === 'edit';
  const routineId = isEdit ? mode.routineId : null;
  const [activeTab, setActiveTab] = useState<RoutineDetailTab>(initialEditTab);
  const [isMemoryViewerOpen, setIsMemoryViewerOpen] = useState(false);
  const [createCollaborations, setCreateCollaborations] = useState<Array<Collaboration>>([]);
  const [createLLMFeatureAccess, setCreateLLMFeatureAccess] = useState<Array<LLMFeatureAccessUpdate> | null>(
    null
  );
  const [dataAccessReady, setDataAccessReady] = useState(false);
  const [collaboratorsCanSave, setCollaboratorsCanSave] = useState(true);
  const collaboratorsStepEnabled = llmEnableRoutinesSharing === true || enableCreateWizard === true;
  const handleAgentHomepageClick = useCallback(() => {
    if (routineId != null && navigateToURL(agentHomepageUrl(routineId))) {
      onAgentHomepageNavigate();
    }
  }, [onAgentHomepageNavigate, routineId]);

  useEffect(() => {
    if (!runsTabEnabled && activeTab === 'runs') {
      setActiveTab('details');
    }
  }, [activeTab, runsTabEnabled]);

  useEffect(() => {
    setActiveTab(initialEditTab);
  }, [initialEditTab]);

  const {
    routine: routineData,
    isLoading,
    refetch,
  } = useRoutine({
    routineId,
    enabled: isEdit,
  });

  const defaultCreateState = useMemo(
    () => getDefaultCreateState(timeBasedEnabled, eventBasedEnabled, llmEnableAgentMemory === true),
    [timeBasedEnabled, eventBasedEnabled, llmEnableAgentMemory]
  );

  const initialFormState = useMemo(() => {
    let state: RoutineFormState;
    if (!isEdit) {
      if (mode.type === 'create' && mode.example != null) {
        // Examples are ready-to-save prefills, so default their scope like a loaded routine.
        state = {
          ...mode.example,
          runAs: isRoutineRunAsDisabled(mode.example.runAs) ? defaultCreateState.runAs : mode.example.runAs,
          triggerConfig: withDefaultObjectScopes(
            mode.example.triggerConfig,
            mode.example.triggerType,
            authorshipEnabled
          ),
        };
      } else {
        state = defaultCreateState;
      }
    } else {
      state = buildFormStateFromQuery(routineData, authorshipEnabled) ?? defaultCreateState;
    }
    return state;
  }, [isEdit, mode, routineData, defaultCreateState, authorshipEnabled]);

  const {formState, dispatch, isDirty, reset} = useRoutineForm(initialFormState);
  const savedFolderIds =
    isEdit && initialFormState.triggerType === TriggerType.EVENT_BASED
      ? ((initialFormState.triggerConfig as Partial<EventTriggerConfig>).folder_ids ?? [])
      : [];
  const dataAccessStepEnabled = formState.runAs === RoutineRunAs.SERVICE_ACCOUNT;
  const editDataAccessEnabled = isEdit && dataAccessStepEnabled;
  const renderedActiveTab =
    (!runsTabEnabled && activeTab === 'runs') || (!editDataAccessEnabled && activeTab === 'data-access')
      ? 'details'
      : activeTab;

  // Re-sync from the server only when the derived state changes by value. Apollo can re-emit
  // `routineData` as a new reference holding stale fields (e.g. the post-save list refetch touches
  // the shared cache entity before this routine's refetch lands), so resetting on every reference
  // change would briefly override the user's edits with the stale value (a visible flicker).
  const lastAppliedServerStateRef = useRef<RoutineFormState | null>(null);
  useEffect(() => {
    if (isEdit && routineData != null) {
      const newState = buildFormStateFromQuery(routineData, authorshipEnabled);
      if (
        newState != null &&
        (lastAppliedServerStateRef.current == null ||
          !shallowFormEqual(newState, lastAppliedServerStateRef.current))
      ) {
        lastAppliedServerStateRef.current = newState;
        reset(newState);
      }
    }
  }, [isEdit, routineData, reset, authorshipEnabled]);

  const {triggers: allowedEventTriggers} = useAllowedEventTriggers({enabled: eventBasedEnabled});

  const mutations = useRoutineMutations(onMutationSuccess);

  const handleNameChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_NAME', value});
    },
    [dispatch]
  );
  const handleDescriptionChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_DESCRIPTION', value});
    },
    [dispatch]
  );
  const handlePromptChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_PROMPT', value});
    },
    [dispatch]
  );
  const handleRemoveFileBlock = useCallback(
    (index: number) => {
      dispatch({type: 'REMOVE_FILE_BLOCK', index});
    },
    [dispatch]
  );
  const handleFileUploaded = useCallback(
    (fileBlock: FileMessageBlock) => {
      dispatch({type: 'ADD_FILE_BLOCK', fileBlock});
    },
    [dispatch]
  );
  const {uploadFile, isUploading} = useRoutineFileUpload(handleFileUploaded);
  const handleTriggerTypeChange = useCallback(
    (value: TriggerType) => {
      dispatch({type: 'SET_TRIGGER_TYPE', value});
    },
    [dispatch]
  );
  const handleTriggerConfigChange = useCallback(
    (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => {
      dispatch({type: 'UPDATE_TRIGGER_CONFIG', updates});
    },
    [dispatch]
  );
  const handleAccessLevelChange = useCallback(
    (value: AccessLevel) => {
      dispatch({type: 'SET_ACCESS_LEVEL', value});
    },
    [dispatch]
  );
  const handleAgentWorkspaceFolderChange = useCallback(
    (value: string | null) => {
      dispatch({type: 'SET_AGENT_WORKSPACE_FOLDER', value});
    },
    [dispatch]
  );
  const handleMemoryEnabledChange = useCallback(
    (value: boolean) => {
      dispatch({type: 'SET_MEMORY_ENABLED', value});
    },
    [dispatch]
  );
  const handleOpenMemoryViewer = useCallback(() => {
    setIsMemoryViewerOpen(true);
  }, []);
  const handleCloseMemoryViewer = useCallback(() => {
    setIsMemoryViewerOpen(false);
  }, []);
  const handleRunAsChange = useCallback(
    (value: RoutineRunAs) => {
      if (isEdit) {
        dispatch({type: 'SET_OVERRIDE_RUN_AS', value});
        return;
      }
      setDataAccessReady(false);
      dispatch({type: 'SET_RUN_AS', value});
    },
    [dispatch, isEdit]
  );

  const canSaveSetup = canSaveRoutine({
    formState,
    isEdit: false,
    isDirty,
    isSaving: mutations.isSaving,
    authorshipEnabled,
    isUploading,
  });

  const handleSave = useCallback(async () => {
    if (formState.triggerType == null) {
      return;
    }
    const messageBlocks = buildMessageBlocks(formState.prompt, formState.fileBlocks);
    const triggerConfig = sanitizeTriggerConfig(formState.triggerType, formState.triggerConfig);
    // The empty-string folder marker is a UI-only "folder limit on, folder pending" state that save
    // validation blocks; normalize it to null so it never reaches the backend.
    const agentWorkspaceFolderId =
      formState.agentWorkspaceFolderId === '' ? null : formState.agentWorkspaceFolderId;
    if (isEdit && routineId != null) {
      const success = await mutations.updateRoutine(routineId, {
        name: formState.name.trim(),
        description: formState.description,
        messageBlocks,
        triggerConfig,
        allowedApprovalCategories: formState.allowedApprovalCategories,
        agentWorkspaceFolderId,
        memoryEnabled: formState.memoryEnabled,
      });
      if (success) {
        await refetch();
      }
    } else {
      const nonOwnerCollaborations = createCollaborations.filter((c) => !c.fromOwnership);
      const collaborations: Array<CollaborationCreateOrUpdate> | undefined =
        collaboratorsStepEnabled && nonOwnerCollaborations.length > 0
          ? convertToCollaborationCreateOrUpdates(nonOwnerCollaborations)
          : undefined;
      const newId = await mutations.createRoutine({
        name: formState.name.trim(),
        description: formState.description || undefined,
        messageBlocks,
        agentType: DEFAULT_AGENT_TYPE,
        triggerType: formState.triggerType,
        triggerConfig,
        allowedApprovalCategories: formState.allowedApprovalCategories,
        agentWorkspaceFolderId,
        runAs: formState.runAs,
        memoryEnabled: formState.memoryEnabled,
        collaborations,
        llmFeatureAccess: dataAccessStepEnabled
          ? (createLLMFeatureAccess ?? []).filter((update) => update.enabled)
          : undefined,
      });
      if (newId != null) {
        if (onRoutineCreated != null) {
          onRoutineCreated(newId);
        } else {
          onClose();
        }
      }
    }
  }, [
    isEdit,
    routineId,
    formState,
    mutations,
    refetch,
    onClose,
    onRoutineCreated,
    createCollaborations,
    createLLMFeatureAccess,
    collaboratorsStepEnabled,
    dataAccessStepEnabled,
  ]);

  const canAdvanceFromCreateStep = useCallback(
    (stepKey: RoutineCreateWizardStep) => {
      if (stepKey === RoutineCreateWizardStep.COLLABORATORS) {
        return collaboratorsCanSave;
      }
      if (stepKey === RoutineCreateWizardStep.DATA_ACCESS) {
        return dataAccessReady;
      }
      return stepKey !== RoutineCreateWizardStep.SETUP || canSaveSetup;
    },
    [canSaveSetup, collaboratorsCanSave, dataAccessReady]
  );

  const resetCollaboratorsCanSave = useCallback(() => {
    setCollaboratorsCanSave(true);
  }, []);

  const stepNav = useCreateStepNav({
    dataAccessStepEnabled,
    collaboratorsStepEnabled,
    initialStepKey: initialCreateWizardStep ?? RoutineCreateWizardStep.SETUP,
    isCreateMode: !isEdit,
    canAdvanceFromStep: canAdvanceFromCreateStep,
    isSaving: mutations.isSaving,
    onFinish: handleSave,
    onCollaboratorsStepExit: resetCollaboratorsCanSave,
  });

  const handlePause = useCallback(async () => {
    if (routineId != null) {
      await mutations.pauseRoutine(routineId);
      await refetch();
    }
  }, [routineId, mutations, refetch]);

  const handleResume = useCallback(async () => {
    if (routineId != null) {
      await mutations.resumeRoutine(routineId);
      await refetch();
    }
  }, [routineId, mutations, refetch]);

  const handleArchive = useCallback(async () => {
    if (routineId != null) {
      const success = await mutations.archiveRoutine(routineId, 'Retired');
      if (success) {
        onClose();
      }
    }
  }, [routineId, mutations, onClose]);

  const handleViewAuditLog = useCallback(() => {
    if (routineId != null) {
      exportAuditLogs([routineId]);
    }
  }, [routineId]);

  const canViewMemory = isEdit && routineId != null && llmEnableAgentMemory === true;

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <Text size="sm" color="dimmed">
          Loading...
        </Text>
      </div>
    );
  }

  const handleCopyRequest = (): void => {
    assert(routineId != null, 'Routine ID is required to copy a routine');
    onCopyRequest(formState, routineId);
  };

  const isArchived = routineData?.archiveData.archived === true;
  const canWrite = routineData?.permissions.writable === true && !isArchived;
  const canArchive = routineData?.permissions.archivable === true && !isArchived;
  const canEdit = routineData != null && canWrite && canCurrentUserModifyRoutine(routineData, 'edit');
  const canPause = routineData != null && canWrite && canCurrentUserModifyRoutine(routineData, 'pause');
  const canResume = routineData != null && canWrite && canCurrentUserModifyRoutine(routineData, 'resume');

  const routineForm = (
    <RoutineForm
      isEdit={isEdit}
      formState={formState}
      savedFolderIds={savedFolderIds}
      onNameChange={handleNameChange}
      onDescriptionChange={handleDescriptionChange}
      onPromptChange={handlePromptChange}
      onRemoveFileBlock={handleRemoveFileBlock}
      onAddFile={uploadFile}
      isUploading={isUploading}
      onTriggerTypeChange={handleTriggerTypeChange}
      onTriggerConfigChange={handleTriggerConfigChange}
      onAccessLevelChange={handleAccessLevelChange}
      onAgentWorkspaceFolderChange={handleAgentWorkspaceFolderChange}
      onMemoryEnabledChange={handleMemoryEnabledChange}
      onViewMemory={canViewMemory ? handleOpenMemoryViewer : undefined}
      onRunAsChange={handleRunAsChange}
      onCopyRequest={handleCopyRequest}
      isDirty={isDirty}
      isSaving={mutations.isSaving}
      error={mutations.error}
      onSave={handleSave}
      onCancel={onClose}
      onPause={handlePause}
      onResume={handleResume}
      onArchive={handleArchive}
      onViewAuditLog={handleViewAuditLog}
      currentStatus={routineData?.status}
      currentRunAs={routineData?.runAs}
      allowedEventTriggers={allowedEventTriggers}
      timeBasedEnabled={timeBasedEnabled}
      eventBasedEnabled={eventBasedEnabled}
      canArchive={canArchive}
      canEdit={canEdit}
      canPause={canPause}
      canResume={canResume}
    />
  );

  if (!isEdit) {
    return (
      <Stack className={classes.panel} spacing="0">
        <Box className={classes.createHeader}>
          <BackNavHeader onClick={onClose} className={classes.backNavHeader} />

          <Title order={4}>Create a new agent</Title>
          {stepNav.showStepNav && (
            <Box className={classes.stepNav}>
              <StepNav<RoutineCreateWizardStep>
                steps={stepNav.enabledSteps}
                statusByStep={stepNav.statusByStep}
                currentStepKey={stepNav.currentStepKey}
                onUpdateCurrentStep={stepNav.setCurrentStepKey}
                size={StepNavSize.FULL_WIDTH}
                variant={StepNavVariant.FLAT}
              />
            </Box>
          )}
        </Box>
        {stepNav.isCurrentStep(RoutineCreateWizardStep.SETUP) && (
          <Box
            className={classes.tabPanel}
            p="0" /* padding will be handled by routineForm's scroll container */
          >
            {routineForm}
          </Box>
        )}
        {stepNav.isCurrentStep(RoutineCreateWizardStep.DATA_ACCESS) && (
          <Box className={classes.tabPanel}>
            <RoutineDataAccess
              mode="create"
              updates={createLLMFeatureAccess}
              onUpdatesChange={setCreateLLMFeatureAccess}
              onReadyChange={setDataAccessReady}
            />
          </Box>
        )}
        {stepNav.isCurrentStep(RoutineCreateWizardStep.COLLABORATORS) && (
          <Box className={classes.tabPanel}>
            <RoutineCreateCollaboratorsStep
              runAs={formState.runAs}
              collaborations={createCollaborations}
              onCollaborationsChange={setCreateCollaborations}
              onCollaboratorsCanSaveChange={setCollaboratorsCanSave}
            />
          </Box>
        )}
        {mutations.error != null && !stepNav.isCurrentStep(RoutineCreateWizardStep.SETUP) && (
          <Alert color="red" variant="light" role="alert">
            {mutations.error}
          </Alert>
        )}
        <Group position="apart" className={classes.createFooter}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={mutations.isSaving}>
            Cancel setup
          </Button>
          <Group spacing="xs">
            {stepNav.footer.back != null && (
              <Button
                variant="secondary"
                size="sm"
                onClick={stepNav.footer.back.onClick}
                disabled={stepNav.footer.back.disabled}
              >
                {stepNav.footer.back.label}
              </Button>
            )}
            {stepNav.footer.primary != null && (
              <Button
                variant="primary"
                size="sm"
                onClick={stepNav.footer.primary.onClick}
                disabled={stepNav.footer.primary.disabled}
                loading={stepNav.footer.primary.loading}
              >
                {stepNav.footer.primary.label}
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    );
  }

  if (isMemoryViewerOpen && canViewMemory) {
    return (
      <div className={classes.panel}>
        <MemoryViewerPanel
          agentName={formState.name}
          routineId={routineId}
          onClose={handleCloseMemoryViewer}
        />
      </div>
    );
  }

  assert(routineId != null, 'Edit mode requires a routine id');

  return (
    <Stack
      {...getTestProps({component: 'RoutineDetailPanel', element: 'root'})}
      className={classes.panel}
      spacing="0"
    >
      <Stack
        spacing={2}
        sx={(theme) => ({
          paddingTop: theme.spacing.md,
          paddingBottom: theme.spacing.md,
          paddingLeft: theme.spacing['3xl'],
          paddingRight: theme.spacing['3xl'],
        })}
      >
        <Group position="apart" className={classes.backNavHeader}>
          <BackNavHeader onClick={onClose} />
          {llmEnableRoutineHomepage === true && (
            <Tooltip label="Agent homepage">
              <ActionIcon
                aria-label="Agent homepage"
                onClick={handleAgentHomepageClick}
                size="sm"
                variant="ghost"
              >
                <BenchlingIcon icon="home-dashboard" />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <Text size="xl" fw={600}>
          {formState.name}
        </Text>
        {routineData?.createdAt != null && (
          <Text size="xs" c="dimmed">
            {(() => {
              const formattedDate = DateUtil.readableTime(routineData.createdAt, {showTime: false});
              const creatorLabel =
                routineData.creator != null ? getPartyDisplayString(routineData.creator) : undefined;
              return creatorLabel != null && creatorLabel !== ''
                ? `Created by ${creatorLabel} on ${formattedDate}`
                : `Created ${formattedDate}`;
            })()}
          </Text>
        )}
      </Stack>

      <Tabs
        className={classes.tabs}
        styles={{
          tab: {
            fontSize: theme.fontSizes.sm,
            fontWeight: 500,
            padding: theme.spacing.md,
            '&[data-active]': {
              borderBottomWidth: 3,
            },
          },
        }}
        value={renderedActiveTab}
        onTabChange={(value) => {
          const nextTab = (value as RoutineDetailTab | null) ?? 'details';
          setActiveTab(nextTab);
        }}
        keepMounted={false}
      >
        <Tabs.List className={classes.tabsList}>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          {editDataAccessEnabled && <Tabs.Tab value="data-access">Data access</Tabs.Tab>}
          {llmEnableRoutinesSharing === true && <Tabs.Tab value="access">Manage access</Tabs.Tab>}
          {runsTabEnabled && <Tabs.Tab value="runs">Runs</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel
          className={cx(classes.tabPanel)}
          value="details"
          p="0" /* padding will be handled by routineForm's scroll container */
        >
          {routineForm}
        </Tabs.Panel>
        {editDataAccessEnabled && (
          <Tabs.Panel className={classes.tabPanel} value="data-access">
            <RoutineDataAccess mode="edit" routineId={routineId} canEdit={canWrite} />
          </Tabs.Panel>
        )}
        {llmEnableRoutinesSharing === true && (
          <Tabs.Panel className={classes.tabPanel} value="access">
            <RoutineAccessTab routineId={routineId} />
          </Tabs.Panel>
        )}
        {runsTabEnabled && (
          <Tabs.Panel className={classes.tabPanel} value="runs">
            {formState.triggerType != null && (
              <RoutineRunsTab
                routineId={routineId}
                routineName={formState.name}
                triggerType={formState.triggerType}
              />
            )}
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}
